import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { getFtdTokenUrl } from '../shared/utils/api-urls.utils';
import { environment } from '../environments/environment'; // adjust path as needed

declare global {
  interface Window {
    postData?: () => Promise<{ payment_token_id?: string }>;
  }
}

export interface CreateHppSessionRequest {
  loanNumber: string;
  amount: string; // "150.00"
  zip: string; // "10001"
}

export interface CreateHppSessionResponse {
  information: string; // HPP redirect URL
  transactionReferenceId: string; // REF...
  message?: string;
}

export interface DebitWithTokenRequest {
  loanNumber: string;
  amount: string; // net amount "150.00"
  zip: string;
  paymentTokenId: string; // from FTD payment_token_id
}

@Injectable({ providedIn: 'root' })
export class IpospayService {
  private loadPromise: Promise<void> | null = null;
  private readonly scriptId = 'ftd';
  private readonly ftdSrc =
    'https://payment.ipospays.tech/ftd/v1/freedomtodesign.js';

  // inject() style to match your other services
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  // ---------------------------
  // HPP (Hosted Payment Page)
  // ---------------------------

  /**
   * Create an HPP session on the backend (backend hardcodes URLs/flags).
   * Backend returns `information` = the redirect URL.
   */
  createHppSession(
    body: CreateHppSessionRequest,
  ): Observable<CreateHppSessionResponse> {
    return this.http.post<CreateHppSessionResponse>(
      `${environment.API_BASE_URL}/ipospay/hpp/create`,
      body,
      { headers: this.authHeaders() },
    );
  }

  // Optional convenience helper if you prefer Promise style in components
  async createHppSessionOnce(
    body: CreateHppSessionRequest,
  ): Promise<CreateHppSessionResponse> {
    return await firstValueFrom(this.createHppSession(body));
  }

  // ---------------------------
  // FTD (tokenization script)
  // ---------------------------

  /** Ensure the FTD script is loaded with a valid token from backend */
  async ensureLoaded(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = (async () => {
      // 1) get short-lived token from backend with auth header
      const tokenResp = await firstValueFrom(
        this.http.get<{ token: string }>(getFtdTokenUrl(), {
          headers: this.authHeaders(),
          // withCredentials: true, // enable only if you use cookie auth
        }),
      );

      const token = tokenResp?.token;
      if (!token) throw new Error('No security token returned from backend');

      // 2) remove any previous script to avoid stale tokens
      const existing = document.getElementById(this.scriptId);
      if (existing) existing.remove();

      // 3) inject script with token
      await this.injectScriptWithToken(token);
    })();

    return this.loadPromise;
  }

  /**
   * If you still use the FTD global `postData()` flow anywhere,
   * this wraps it safely.
   */
  async tokenizeViaFtd(): Promise<{ payment_token_id?: string }> {
    await this.ensureLoaded();
    if (!window.postData) throw new Error('FTD postData() is not available');
    return await window.postData();
  }

  debitWithToken(body: DebitWithTokenRequest): Observable<any> {
    return this.http.post<any>(
      `${environment.API_BASE_URL}/ipospay/debit/token`,
      body,
      { headers: this.authHeaders() },
    );
  }

  // ---------------------------
  // Internals
  // ---------------------------

  private authHeaders(): HttpHeaders {
    const token = this.auth.token;
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private injectScriptWithToken(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // remove any previous FTD tag by the id the lib expects
      const old = document.getElementById(this.scriptId);
      if (old) old.remove();

      const s = document.createElement('script');
      s.id = this.scriptId; // IMPORTANT: the library looks for this id
      s.src = this.ftdSrc;
      s.defer = true;
      s.setAttribute('security_key', token); // attribute the lib reads
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load FTD script'));
      document.head.appendChild(s);
    });
  }
}
